import SectionHeader from "@/components/SectionHeader";
import { useStation } from "@/hooks/useWeather";
import { MapPin, Mountain, Compass, Camera, Radio } from "lucide-react";
import { motion } from "framer-motion";

const GALLERY = [
  { src: "/stazione/foto4.jpg", caption: "La stazione in Via Loreto" },
  { src: "/stazione/foto3.webp", caption: "Vista su Palestrina dalla stazione" },
  { src: "/stazione/foto2.jpg", caption: "Sensore Davis Vantage Pro2" },
  { src: "/stazione/foto1.webp", caption: "La stazione sotto la neve" },
];

export default function Stazione() {
  const { data } = useStation();

  return (
    <div data-testid="page-stazione" className="pt-28 pb-24">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <SectionHeader
          eyebrow="La stazione"
          title="Palestrina, Via Loreto."
          subtitle="Un presidio meteorologico installato a ridosso della zona Marcigliana, cerniera fra il centro storico e la campagna prenestina."
        />

        <div className="grid lg:grid-cols-3 gap-6 mb-14">
          <StatCard icon={MapPin} label="Indirizzo" value={data?.address ?? "Via Loreto, Palestrina"} />
          <StatCard icon={Mountain} label="Altitudine" value={data ? `${data.altitude_m} m s.l.m.` : "—"} />
          <StatCard icon={Compass} label="Coordinate" value={data ? `${data.latitude.toFixed(4)}° N · ${data.longitude.toFixed(4)}° E` : "—"} />
        </div>

        {/* Description */}
        <div className="grid lg:grid-cols-5 gap-10 mb-16">
          <div className="lg:col-span-3">
            <h3 className="font-display text-3xl text-[#0B132B] mb-4">La mia stazione, aperta a tutti.</h3>
            <p className="text-slate-600 leading-relaxed">
              {data?.description ??
                "Stazione meteorologica personale installata in Via Loreto, a Palestrina."}
            </p>
            <p className="text-slate-600 leading-relaxed mt-4">
              La postazione si trova a quota <strong>404 m s.l.m.</strong>, in una fascia altimetrica leggermente
              inferiore rispetto al centro storico e aperta verso i quadranti occidentali. Questo la rende un buon
              osservatorio per le <strong>inversioni termiche notturne</strong>, l&apos;accumulo di aria fredda nelle
              conche del fondovalle e il microclima locale che caratterizza questo specifico punto del territorio prenestino.
            </p>
          </div>
          <div className="lg:col-span-2">
            <div className="rounded-3xl overflow-hidden shadow-lg border border-slate-200 aspect-[4/3]" data-testid="station-map">
              <iframe
                title="Mappa Palestrina Via Loreto"
                className="w-full h-full"
                src="https://www.openstreetmap.org/export/embed.html?bbox=12.87%2C41.815%2C12.91%2C41.836&layer=mapnik&marker=41.825544%2C12.889484"
                loading="lazy"
              />
            </div>
          </div>
        </div>

        {/* Instrumentation */}
        <SectionHeader eyebrow="Strumentazione" title="Tecnologia al servizio del dato." />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-16" data-testid="instruments-grid">
          {(data?.instruments ?? []).map((it, i) => (
            <motion.div
              key={it.name}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.05 }}
              className="metric-card p-6"
            >
              <span className="h-10 w-10 rounded-full flex items-center justify-center bg-[#0EA5E9]/10 text-[#0EA5E9]">
                <Radio className="h-5 w-5" />
              </span>
              <p className="mt-4 font-medium text-[#0B132B]">{it.name}</p>
              <p className="text-sm text-slate-500 mt-1">{it.detail}</p>
            </motion.div>
          ))}
        </div>

        {/* Gallery */}
        <SectionHeader eyebrow="Galleria" title="La stazione in immagini." />
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="station-gallery">
          {GALLERY.map((photo, i) => (
            <motion.div
              key={photo.src}
              initial={{ opacity: 0, scale: 0.98 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.06 }}
              className="relative aspect-[4/5] rounded-2xl overflow-hidden group"
            >
              <img src={photo.src} alt={photo.caption} className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0B132B]/80 via-transparent to-transparent" />
              <div className="absolute bottom-4 left-4 right-4 text-white text-xs">
                <Camera className="h-3.5 w-3.5 inline mr-1.5 text-[#FBBF24]" />
                <span className="font-medium">{photo.caption}</span>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="metric-card p-6 flex items-center gap-4">
      <span className="h-12 w-12 rounded-full bg-[#0B132B] text-[#FBBF24] flex items-center justify-center">
        <Icon className="h-5 w-5" />
      </span>
      <div>
        <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500">{label}</p>
        <p className="font-medium text-[#0B132B] mt-0.5">{value}</p>
      </div>
    </div>
  );
}
